import React, { useEffect, useMemo, useState } from 'react';

import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Form,
  GetProp,
  Image,
  Input,
  Modal,
  Row,
  Select,
  Upload,
  UploadFile,
  UploadProps,
  message,
} from 'antd';
import PhoneInput from 'antd-phone-input';
import { UploadChangeParam } from 'antd/es/upload';
import { RcFile } from 'antd/es/upload/interface';
import { AxiosProgressEvent } from 'axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import appI18n from '../../../../config/i18n';
import { ApiErrorData } from '../../../../models/ApiResponse';
import { Company } from '../../../../models/Company';
import { Contact } from '../../../../models/Contact';
import { Country } from '../../../../models/Country';
import { ItemInterface } from '../../../../models/ItemInterface';
import { User } from '../../../../models/User';
import { ContactService } from '../../../../services/ContactService';
import { FunctionsHelper } from '../../../../utils/FunctionsHelper';
import {
  resolveCompanyIds,
  visibilityFromCompanyIds,
} from '../contactCompanies';
import ContactVisibilityField from './ContactVisibilityField';

interface DetailContactModalProps {
  id: number;
  isOpen: boolean;
  handleModalCancel: () => void;
  submit: (
    id: number,
    companyIds: number[],
    fullName: string,
    position: string,
    email: string,
    dialCode: string,
    phone: string,
    avatarUrl: string
  ) => void;
  countries: Country[];
  companies: Company[];
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

function DetailContactModal({
  id,
  isOpen,
  handleModalCancel,
  submit,
  countries,
  companies,
}: DetailContactModalProps) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const authUser: User = useSelector((state: any) => state.auth);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    setLoading(false);
    form.resetFields();
    setSelectedCountry(null);
    setFileList([]);

    form.setFieldValue('phone', {
      ...form.getFieldValue('phone'),
      countryCode: parseInt(
        FunctionsHelper.getDialCode(authUser.company.countryCode)
      ),
    });

    if (isOpen) {
      fetchData(id);
    }
  }, [isOpen]);

  const onFinish = async (values: any) => {
    if (fileList.length === 0) {
      messageApi.error(
        t(
          'scenes.settings.contacts.components.detail_contact_modal.messages.avatar_upload_required'
        )
      );
      return;
    }

    const file = fileList[0];
    const logoUrl = file.response?.url;
    const statusFile = file.status;

    switch (statusFile) {
      case 'uploading':
        messageApi.info(
          t(
            'scenes.settings.contacts.components.detail_contact_modal.messages.avatar_uploading'
          )
        );
        return;
      case 'error':
        messageApi.error(
          t(
            'scenes.settings.contacts.components.detail_contact_modal.errors.avatar_upload_failed'
          )
        );
        return;
    }

    if (!logoUrl) {
      messageApi.error(
        t(
          'scenes.settings.contacts.components.detail_contact_modal.errors.avatar_upload_failed'
        )
      );
      return;
    }

    setLoading(true);

    await submit(
      id,
      resolveCompanyIds(values.visibility, values.company),
      values.fullName,
      values.position,
      values.email,
      `+${values.phone.countryCode}`,
      `${values.phone.areaCode}${values.phone.phoneNumber}`,
      logoUrl
    );

    setLoading(false);
  };

  const fetchData = async (contactId: number) => {
    setLoading(true);
    const response = await ContactService.findById(contactId);

    if (response.success) {
      const contact = response.data as Contact;

      form.setFieldValue('country', contact.countryCode);
      setSelectedCountry(contact.countryCode);
      form.setFieldValue('fullName', contact.fullName);
      form.setFieldValue('position', contact.position);
      form.setFieldValue('email', contact.email);

      if (contact.phone && contact.dialCode) {
        const newPhone = {
          ...form.getFieldValue('phone'),
          countryCode: parseInt(contact.dialCode),
          areaCode: null,
          phoneNumber: null,
        };

        if (contact.phone) {
          newPhone.areaCode = contact.phone;
        }

        form.setFieldValue('phone', newPhone);
      }

      if (contact.avatarUrl) {
        setFileList([
          {
            uid: '-1',
            name: FunctionsHelper.getFilenameFromUrl(contact.avatarUrl),
            status: 'done',
            url: contact.avatarUrl,
            response: {
              url: contact.avatarUrl,
            },
          },
        ]);
      }

      // El país es de solo lectura en este modal: no hay reseteo al cambiarlo,
      // así que las compañías del contacto se aplican directamente.
      form.setFieldValue('company', contact.companyIds ?? []);
      form.setFieldValue(
        'visibility',
        visibilityFromCompanyIds(contact.companyIds)
      );

      setLoading(false);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          'Hubo un error al intentar obtener los datos del contacto, por favor inténtalo nuevamente.',
        3.5
      );
      setTimeout(() => {
        handleModalCancel();
      }, 3500);
    }
  };

  const countryOptions = useMemo<ItemInterface[]>(() => {
    return countries.map((record) => ({
      value: record.code,
      label: record.name,
    }));
  }, [countries]);

  const filteredCompanies = useMemo<ItemInterface[]>(() => {
    if (!selectedCountry) return [];
    return companies
      .filter((record) => record.countryCode === selectedCountry)
      .map((record) => ({
        value: record.id,
        label: record.name,
      }));
  }, [selectedCountry, companies]);

  const handleChangeCountry = (newValue?: string) => {
    let dialCode = FunctionsHelper.getDialCode(authUser.company.countryCode);

    if (newValue) {
      const country = countries.find((record) => {
        return record.code === newValue;
      });

      if (country) {
        dialCode = FunctionsHelper.getDialCode(country.code);
        setSelectedCountry(newValue);
      }
    } else {
      setSelectedCountry(null);
    }

    if (!form.getFieldValue('phone').phoneNumber) {
      form.setFieldValue('phone', {
        ...form.getFieldValue('phone'),
        countryCode: parseInt(dialCode),
      });
    }
  };

  const beforeUpload = (file: FileType) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      messageApi.error(
        t(
          'scenes.settings.contacts.components.detail_contact_modal.avatar.allowed_type_error'
        )
      );
      return Upload.LIST_IGNORE;
    }
    const isLt10MB = file.size / 1024 / 1024 <= 4;
    if (!isLt10MB) {
      messageApi.error(
        t(
          'scenes.settings.contacts.components.detail_contact_modal.avatar.max_size_error'
        )
      );
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleChange = ({
    fileList: newFileList,
  }: UploadChangeParam<UploadFile>) => {
    setFileList(newFileList);
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const uploadProps: UploadProps = {
    beforeUpload: beforeUpload,
    fileList,
    listType: 'picture-card',
    showUploadList: true,
    onChange: handleChange,
    maxCount: 1,
    onPreview: handlePreview,
  };

  return (
    <>
      {contextHolder}

      <Modal
        title={t(
          'scenes.settings.contacts.components.detail_contact_modal.title',
          { id }
        )}
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={480}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="country"
                label={t(
                  'scenes.settings.contacts.components.detail_contact_modal.labels.country'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.contacts.components.detail_contact_modal.validations.country.required'
                    ),
                  },
                ]}
              >
                <Select
                  style={{ width: '100%' }}
                  options={countryOptions}
                  disabled={true}
                  placeholder={t(
                    'scenes.settings.contacts.components.detail_contact_modal.placeholders.select_option'
                  )}
                  onChange={(value) => handleChangeCountry(value)}
                />
              </Form.Item>
            </Col>
          </Row>

          <ContactVisibilityField
            i18nPrefix="scenes.settings.contacts.components.detail_contact_modal"
            companyOptions={filteredCompanies}
            disabled={loading}
            companyDisabled={!selectedCountry}
          />

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="fullName"
                label={t(
                  'scenes.settings.contacts.components.detail_contact_modal.labels.full_name'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.contacts.components.detail_contact_modal.validations.full_name.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.contacts.components.detail_contact_modal.placeholders.full_name'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="position"
                label={t(
                  'scenes.settings.contacts.components.detail_contact_modal.labels.position'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.contacts.components.detail_contact_modal.validations.position.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.contacts.components.detail_contact_modal.placeholders.position'
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="email"
                label={t(
                  'scenes.settings.contacts.components.detail_contact_modal.labels.email'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.contacts.components.detail_contact_modal.validations.email.required'
                    ),
                  },
                  {
                    type: 'email',
                    message: t(
                      'scenes.settings.contacts.components.detail_contact_modal.validations.email.invalid'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.contacts.components.detail_contact_modal.placeholders.email'
                  )}
                  addonAfter={'@'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="phone"
                label={t(
                  'scenes.settings.contacts.components.detail_contact_modal.labels.phone'
                )}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value.valid()) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.settings.contacts.components.detail_contact_modal.validations.phone.invalid'
                          )
                        )
                      );
                    },
                  },
                ]}
              >
                <PhoneInput
                  enableSearch
                  searchNotFound={t(
                    'scenes.settings.contacts.components.detail_contact_modal.phone.search_not_found'
                  )}
                  searchPlaceholder={t(
                    'scenes.settings.contacts.components.detail_contact_modal.phone.search_placeholder'
                  )}
                  placeholder={t(
                    'scenes.settings.contacts.components.detail_contact_modal.placeholders.phone'
                  )}
                  disabled={loading}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                label={t(
                  'scenes.settings.contacts.components.detail_contact_modal.labels.avatar'
                )}
                required={true}
              >
                <Upload
                  {...uploadProps}
                  disabled={loading}
                  customRequest={async ({
                    file,
                    onSuccess,
                    onError,
                    onProgress,
                  }: any) => {
                    const formData = new FormData();
                    formData.append('file', file as RcFile);

                    try {
                      const response = await ContactService.uploadAvatarFile(
                        formData,
                        (progressEvent: AxiosProgressEvent) => {
                          if (onProgress && progressEvent.total) {
                            const percent = Math.round(
                              (progressEvent.loaded * 100) / progressEvent.total
                            );
                            onProgress({ percent });
                          }
                        }
                      );

                      if (response.success) {
                        onSuccess?.(response.data);
                      } else {
                        const error = response.data as ApiErrorData;
                        throw new Error(
                          error.message ||
                            t(
                              'scenes.settings.contacts.components.detail_contact_modal.errors.avatar_upload_failed'
                            )
                        );
                      }
                    } catch (error: any) {
                      onError?.(error);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <button
                    style={{ border: 0, background: 'none', cursor: 'pointer' }}
                    type="button"
                    disabled={loading}
                  >
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>
                      {t(
                        'scenes.settings.contacts.components.detail_contact_modal.buttons.upload_image'
                      )}
                    </div>
                  </button>
                </Upload>
              </Form.Item>
              {previewImage && (
                <Image
                  wrapperStyle={{ display: 'none' }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) =>
                      !visible && setPreviewImage(''),
                  }}
                  src={previewImage}
                  onDrop={() => {
                    form.setFieldValue('avatar', undefined);
                    console.log('util');
                  }}
                />
              )}
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => form.submit()}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.settings.contacts.components.detail_contact_modal.buttons.save_changes'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default DetailContactModal;
